class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    const classList = this.mainDetailsToggle.classList;
    this.isClickMegaMenu = classList.contains('mega-menu--click');
    this.isHoverMegaMenu = classList.contains('mega-menu--hover') || classList.contains('mega-menu--hover_aligned');
    this.isAlignedMegaMenu = classList.contains('mega-menu--hover_aligned');
    this.isAnimatedMegaMenu = this.isClickMegaMenu || this.isHoverMegaMenu;
    this.summary = this.mainDetailsToggle.querySelector('summary');

    // Tiempos configurables desde los settings del header (data-* en el <nav> del mega menú)
    const nav = this.closest('[data-mega-menu-behavior]');
    const readDelay = (name, fallback) => {
      const value = parseInt(nav?.dataset[name], 10);
      return Number.isNaN(value) ? fallback : value;
    };
    this.openDelay = readDelay('openDelay', 50);
    this.switchDelay = readDelay('switchDelay', 150);
    this.closeDelay = readDelay('closeDelay', 240);
    this.exitDuration = readDelay('exitDuration', 200);

    if (this.isClickMegaMenu) {
      this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    }

    if (this.isHoverMegaMenu) {
      this.mainDetailsToggle.addEventListener('pointerenter', this.onPointerEnter.bind(this));
      this.mainDetailsToggle.addEventListener('pointerleave', this.onPointerLeave.bind(this));
      this.summary.addEventListener('click', this.onHoverSummaryClick.bind(this));
    }
  }

  // Mega menú hover: un click con mouse en el padding del summary navega como el link padre,
  // en vez de cerrar el menú que está bajo el cursor. Con teclado (detail 0) se mantiene el toggle nativo.
  onHoverSummaryClick(event) {
    if (event.detail === 0 || event.target.closest('a')) return;
    event.preventDefault();
    this.summary.querySelector('a')?.click();
  }

  // Mega menú hover: intención de hover. Pasar sobre otro link camino al contenido no cambia el menú
  // salvo que el cursor se quede ahí `switchDelay` ms; salir y volver dentro de `closeDelay` no lo cierra.
  onPointerEnter(event) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.closeTimer);

    const details = this.mainDetailsToggle;
    if (details.open && !details.classList.contains('is-closing')) return;

    const delay = this.getOtherOpenMenus().length ? this.switchDelay : this.openDelay;
    clearTimeout(this.openTimer);
    this.openTimer = setTimeout(() => this.open(), delay);
  }

  onPointerLeave(event) {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.openTimer);
    if (!this.mainDetailsToggle.open) return;

    clearTimeout(this.closeTimer);
    this.closeTimer = setTimeout(() => this.close(), this.closeDelay);
  }

  open() {
    const details = this.mainDetailsToggle;

    if (details.classList.contains('is-closing')) {
      // Vuelve a entrar mientras se cerraba: se revierte sin reanimar.
      this.cancelClosing();
      details.classList.add('is-switching');
      this.summary.setAttribute('aria-expanded', true);
      return;
    }

    if (details.open) return;

    const openMenus = this.getOtherOpenMenus();
    if (openMenus.length) {
      // Cambio entre menús: el panel no se reanima, solo su contenido hace un fundido corto.
      details.classList.add('is-switching', 'is-swapping');
      openMenus.forEach((menu) => menu.close({ instant: true }));
    }

    details.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', true);
    if (this.isAlignedMegaMenu) this.alignMegaMenu();
  }

  // Alinea el primer childlink exactamente bajo el link padre desde el que se abrió el mega menú.
  // Si en esa posición el contenido no cabe en una fila, se desplaza a la izquierda hasta quedar alineado a la derecha.
  alignMegaMenu() {
    const details = this.mainDetailsToggle;
    const trigger = details.querySelector('summary > a');
    const list = details.querySelector('.mega-menu__list');
    const firstLink = list && list.querySelector('.mega-menu__link');
    if (!trigger || !firstLink || !list.children.length) return;

    list.style.paddingLeft = '';
    list.classList.add('mega-menu__list--measuring');

    const style = getComputedStyle(list);
    const basePadding = parseFloat(style.paddingLeft) || 0;
    const available = list.getBoundingClientRect().width - basePadding - (parseFloat(style.paddingRight) || 0);
    const rects = [...list.children].map((item) => item.getBoundingClientRect());
    const contentWidth = Math.max(...rects.map((r) => r.right)) - Math.min(...rects.map((r) => r.left));
    const desired = basePadding + trigger.getBoundingClientRect().left - firstLink.getBoundingClientRect().left;
    const maxPadding = basePadding + Math.max(0, available - contentWidth);

    list.classList.remove('mega-menu__list--measuring');
    list.style.paddingLeft = `${Math.max(basePadding, Math.min(desired, maxPadding))}px`;
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      // Mega menú click: al pasar a otro item no se cierra aquí, sino cuando el otro abre (evita pestañeo)
      if (this.isClickMegaMenu && document.activeElement?.closest('header-menu .mega-menu--click')) return;
      this.close();
    });
  }

  // Mega menú click: intercepta el toggle nativo para poder animar el cierre y cambiar de menú sin pestañeo.
  onSummaryClick(event) {
    const details = this.mainDetailsToggle;

    if (details.open && !details.classList.contains('is-closing')) {
      event.preventDefault();
      this.close();
      return;
    }

    if (details.classList.contains('is-closing')) {
      // Reabrir durante la animación de cierre: se revierte sin cerrar.
      event.preventDefault();
      this.cancelClosing();
      details.classList.add('is-switching');
      // Se difiere para quedar después del listener de global.js que también actualiza aria-expanded.
      setTimeout(() => this.summary.setAttribute('aria-expanded', true));
      return;
    }

    const openMenus = this.getOtherOpenMenus();
    if (openMenus.length) {
      // Cambio entre menús: el panel no se reanima (solo fundido del contenido) y el anterior se cierra al instante.
      details.classList.add('is-switching', 'is-swapping');
      openMenus.forEach((menu) => menu.close({ instant: true }));
    }
  }

  getOtherOpenMenus() {
    return [...document.querySelectorAll('header-menu')].filter(
      (menu) => menu !== this && menu.mainDetailsToggle.open
    );
  }

  close({ instant = false } = {}) {
    const details = this.mainDetailsToggle;
    clearTimeout(this.openTimer);
    clearTimeout(this.closeTimer);
    if (!this.isAnimatedMegaMenu || !details.open) return super.close();

    this.summary.setAttribute('aria-expanded', false);

    if (instant || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.finishClosing();
      return;
    }

    if (details.classList.contains('is-closing')) return;

    details.classList.remove('is-switching', 'is-swapping');
    details.classList.add('is-closing');

    this.onClosingEnd = (event) => {
      if (event && event.target !== this.content) return;
      this.finishClosing();
    };
    this.content.addEventListener('animationend', this.onClosingEnd);
    // Respaldo por si la animación no llega a dispararse (p. ej. pestaña en segundo plano).
    this.closingTimeout = setTimeout(this.onClosingEnd, this.exitDuration + 100);
  }

  cancelClosing() {
    clearTimeout(this.closingTimeout);
    if (this.onClosingEnd) this.content.removeEventListener('animationend', this.onClosingEnd);
    this.onClosingEnd = null;
    this.mainDetailsToggle.classList.remove('is-closing');
  }

  finishClosing() {
    this.cancelClosing();
    this.mainDetailsToggle.classList.remove('is-switching', 'is-swapping');
    this.mainDetailsToggle.removeAttribute('open');
  }

  onToggle() {
    if (this.isAnimatedMegaMenu) {
      if (this.mainDetailsToggle.open) {
        this.getOtherOpenMenus().forEach((menu) => menu.close({ instant: true }));
      } else {
        // Cierre externo (p. ej. Escape): limpia estados de animación.
        this.cancelClosing();
        this.mainDetailsToggle.classList.remove('is-switching', 'is-swapping');
      }
    }

    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
